import { Checkbox, Select } from '@truepill/react-capsule'
import { stateAllOption, dashboardStateDefaultOption, LaunchDarkly } from '@truepill/tpos-types'
import { ReactComponent as MapIcon } from 'assets/icons/map.svg'
import IconWrapper from 'components/IconWrapper'
import { useFlag } from 'providers/LaunchDarklyProvider'
import { usePlusClient } from 'providers/VisionRouter'
import { capitalize } from 'utils'
import states from '../../utils/states'

type StatesSelectProps = {
  label: string
  variant?: 'small' | 'large'
}

const StatesSelect = (props: StatesSelectProps): JSX.Element => {
  const displayDashboardDesignV2: boolean = useFlag(LaunchDarkly.FeatureFlags.TPOS_DASHBOARD_DESIGN_V2) ?? false

  const { label, variant = 'large' } = props
  const { currentLocation, routeToMergedQuery } = usePlusClient()

  const noStates = currentLocation?.queryMap?.noStates
  const selectedOptions = currentLocation?.queryMap?.states || []

  const defaultOption = {
    name: 'All states',
    value: stateAllOption,
  }

  const statesOptions: { name: string; value: string }[] = Object.values(states).map(value => ({ name: value, value }))

  const options = [defaultOption, ...statesOptions]

  const allStatesValue = displayDashboardDesignV2 ? dashboardStateDefaultOption : stateAllOption
  const value = selectedOptions.includes(stateAllOption) ? allStatesValue : selectedOptions.join(',')

  if (!noStates && !selectedOptions.length) {
    routeToMergedQuery({ states: [stateAllOption] })
  }

  return (
    <Select
      variant={variant}
      label={label && capitalize(label)}
      css={{
        ul: { maxHeight: '21rem' },
        ...(displayDashboardDesignV2 && variant === 'small' && { width: '14rem' }),
      }}
      value={value}
      onChange={e => {
        // Do not close the dropdown everytime a check is selected (note that e is not an event here
        // and has no preventDefault property, but we just need to prevent default behavior by forcing an exception)
        e.preventDefault()
      }}
      options={options}
      placeholder="Select states"
      selectedKey="name"
      startAdornment={() =>
        displayDashboardDesignV2 ? (
          <IconWrapper>
            <MapIcon />
          </IconWrapper>
        ) : (
          <></>
        )
      }
      optionComponent={({ option }) => (
        <>
          <Checkbox
            label={option?.name}
            checked={selectedOptions.includes(option?.value) || selectedOptions.includes(stateAllOption)}
            onCheckedChange={checked => {
              if (option?.value === stateAllOption) {
                // Toggle between all states and no states. Weather zones should reset with every change to states.
                routeToMergedQuery(
                  checked
                    ? { states: [stateAllOption], noStates: undefined, weatherZones: undefined }
                    : { states: [], noStates: true, weatherZones: undefined },
                )
              } else {
                if (checked) {
                  // Add option to selected states
                  routeToMergedQuery({
                    states: [...selectedOptions, option?.value],
                    noStates: undefined,
                    weatherZones: undefined,
                  })
                } else {
                  const newSelectedOptions = selectedOptions.includes(stateAllOption)
                    ? // If 'All' option was previously selected, select all states except the now unchecked option
                      statesOptions.filter(opt => opt.value !== option?.value).map(opt => opt.value)
                    : // Otherwise remove the option from the selected options
                      selectedOptions.filter((value: string) => value !== option?.value)
                  routeToMergedQuery({
                    states: newSelectedOptions,
                    noStates: undefined,
                    weatherZones: undefined,
                  })
                }
              }
            }}
          />
        </>
      )}
    />
  )
}

export default StatesSelect
